import {PosaRedeemStore, RedeemSectionData} from '../../utils/types';
import React, {Fragment} from 'react';
import {generatePlatformOptions, getCardImage} from '../../utils';

import Dropdown from './DropdownStyled';
import {localizedMessage} from '../message/Message';
import PosaRedeemActions from '../../actions/PosaRedeemActions';
import PosaTemplate from './PosaTemplate';
import PsnCountryContainer from './RedeemSectionPsnCountryContainer';
import RedeemSectionLoggedInStyled from './RedeemSectionLoggedInStyled';
import Tooltip from './Tooltip';
import cx from 'classnames';
import root from 'window-or-global';

const next = () => {
  root.scrollTo(0, 0);
  PosaRedeemActions.setShowConfirm(true);
};

const platformSelection = ({value}) => PosaRedeemActions.setPlatform(value);

interface Props {
  data: RedeemSectionData;
  posaRedeemStore: PosaRedeemStore;
  accountName: string;
}

const VerifiedCodeSection = ({data, posaRedeemStore, accountName}: Props) => {
  const {
    platform,
    code,
    psnCountry,
    externalAuths,
    page: {
      configs: {psnOptions, enableRegionAutoDetection, extra},
    },
    offer,
    codeVerified,
    connectedPlatforms = [],
  } = posaRedeemStore;
  const {
    bgImage,
    moneyImage,
    loggedInContent,
    inputPlaceholder,
    toolTipDescription,
    toolTipImage,
    platformToolTip,
    psnToolTip,
  } = data;
  const platformOptions = React.useMemo(
    () => generatePlatformOptions(externalAuths, localizedMessage, connectedPlatforms),
    [externalAuths, connectedPlatforms]
  );
  const countries = React.useMemo(() => {
    try {
      const countries = JSON.parse(psnOptions);
      if (countries) return countries;
    } catch (ex) {
      console.warn('VerifiedCodeSection: Unable to parse psnOptions');
    }
    return [];
  }, [psnOptions]);

  const style = React.useMemo(
    () => ({
      backgroundImage: `url(${bgImage})`,
    }),
    [bgImage]
  );
  const image = codeVerified ? getCardImage(offer) : moneyImage;

  const dropdownOptions = React.useMemo(
    () =>
      platformOptions.map((op) => {
        const {value, label, disabled, platformDisabled, hasExternalAuth} = op;
        return {
          value,
          label,
          disabled,
          disableMark: platformDisabled && hasExternalAuth ? '**' : '',
        };
      }),
    [platformOptions]
  );
  const extraTag = React.useMemo(() => {
    const hasNoneExternalAuth = platformOptions.find((op) => !op.hasExternalAuth);
    const hasPlatformDisabled = platformOptions.find(
      (op) => op.platformDisabled && op.hasExternalAuth
    );
    return (
      <Fragment>
        {!!hasNoneExternalAuth && (
          <PosaTemplate
            Tag="div"
            isTemplate
            className="ecr-dropdown-row warning disabled"
            string={localizedMessage('epic.code.redemption.posa.platform.authHelp')}
          />
        )}
        {!!hasPlatformDisabled && (
          <PosaTemplate
            Tag="div"
            isTemplate
            className="ecr-dropdown-row warning disabled"
            string={localizedMessage('epic.code.redemption.posa.platform.notRedeemable')}
          />
        )}
      </Fragment>
    );
  }, [platformOptions, extra]);

  return (
    <RedeemSectionLoggedInStyled className={'ecr-redeemSection verified'} style={style}>
      <div className="ecrrs-left">{image && <img src={image} alt="card" />}</div>
      <div className="ecrrs-right">
        <div className="ecrrs-loggedIn verified">
          <PosaTemplate
            posaRedeemStore={posaRedeemStore}
            isTemplate
            Tag="div"
            string={loggedInContent}
            useDangerouslySetInnerHTML
            accountName={accountName}
          />
          <div className="input-wrapper codeInput verified">
            <input type="text" placeholder={inputPlaceholder} value={code} disabled />
            <Tooltip content={toolTipDescription} image={toolTipImage} />
          </div>
          <div className="redeem-next next-select-platform">
            <strong>{localizedMessage('epic.code.redemption.posa.page.common.next')}:</strong>
            <span>{localizedMessage('epic.code.redemption.posa.page.redeem.next')}</span>
          </div>
          <div className={cx('input-wrapper platformInput', {verified: platform})}>
            <Dropdown
              placeholder={localizedMessage(
                'epic.code.redemption.posa.page.redeem.next.placeholder'
              )}
              options={dropdownOptions}
              extra={extraTag}
              callback={platformSelection}
            />
            <Tooltip content={platformToolTip} image="" />
          </div>
          {platform && platform === 'psn' ? (
            <PsnCountryContainer
              externalAuths={externalAuths}
              enableRegionAutoDetection={enableRegionAutoDetection}
              psnCountry={psnCountry}
              countries={countries}
              psnToolTip={psnToolTip}
              connectedPlatforms={connectedPlatforms}
            />
          ) : null}
          {(platform && platform !== 'psn') ||
          (platform && platform === 'psn' && psnCountry && !enableRegionAutoDetection) ? (
            <div className="confirm-next-section">
              <button onClick={next}>
                {localizedMessage('epic.code.redemption.posa.page.common.next')}
              </button>
              <span>{localizedMessage('epic.code.redemption.posa.page.redeem.confirm.next')}</span>
            </div>
          ) : null}
        </div>
      </div>
    </RedeemSectionLoggedInStyled>
  );
};

export default VerifiedCodeSection;
